import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatCurrency from 'components/Common/FormatCurrency'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'
import CruiseCabinTypeGrid from 'components/Cruises/Common/CruiseCabinTypeGrid'
import CruiseCabinTypeTileWrapper from 'components/Cruises/Common/CruiseCabinTypeTileWrapper'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Image from 'components/Common/Image'
import styled from 'styled-components'
import { rem } from 'polished'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { findBookingRateList } from 'api/cruises'
import { sortBy } from 'lib/array/arrayUtils'
import { getCruiseBookingRequestParams } from 'actions/CruiseActions'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'

const ModalContent = styled.div`
  position: relative;
  max-width: ${rem(760)};
`

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(240)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

export interface WeddingGiftRegistryCruiseCabinTypeSelection {
  cabinType: string
  price: App.LowestPriceWithCategory
  imageId?: string
}

export interface WeddingGiftRegistryCruiseCabinRateSelection {
  occupancy: App.Occupants
  sessionId: string
  bookingId: string
  cabinRate: App.Cruises.BookingCabinRatePackage
}

export interface WeddingGiftRegistryCruiseCabinTypeModalResult extends WeddingGiftRegistryCruiseCabinTypeSelection {
  rateSelections: Array<WeddingGiftRegistryCruiseCabinRateSelection>
}

interface Props {
  options: Array<WeddingGiftRegistryCruiseCabinTypeSelection>
  occupancies: Array<App.Occupants>
  offer: App.CruiseOffer
  departureId: string
}

function getOccupancyTravellerCount(occupancy: App.Occupants): number {
  return occupancy.adults + (occupancy.children ?? 0) + (occupancy.teenagers ?? 0)
}

function getOccupanciesTravellerCount(occupancies: Array<App.Occupants>): number {
  return occupancies.reduce((total, occupancy) => total + getOccupancyTravellerCount(occupancy), 0)
}

function WeddingGiftRegistryCruiseCabinTypeModal({ options, occupancies, offer, departureId }: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext<WeddingGiftRegistryCruiseCabinTypeModalResult | undefined>()
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinDescriptions = useCruiseCabinDescriptions()
  const travellerCount = getOccupanciesTravellerCount(occupancies)
  const [loadingCabinDetails, setLoadingCabinDetails] = useState(false)
  const bookingRequestParams = useAppSelector(getCruiseBookingRequestParams, shallowEqual)

  const onSelect = useCallback(async(option: WeddingGiftRegistryCruiseCabinTypeSelection) => {
    setLoadingCabinDetails(true)

    try {
      const rateSelections = await Promise.all(occupancies.map(async(occupancy) => {
        const rateList = await findBookingRateList({
          ...bookingRequestParams,
          departureId,
          travelers: {
            adult: occupancy.adults,
            child: (occupancy.children ?? 0) + (occupancy.infants ?? 0),
            childBirthDate: (occupancy.childrenBirthDate ?? []) as Array<string>,
          },
        }, offer)

        const cabinRate = sortBy(
          rateList.cabinRatePackages.filter(cabinRate => cabinRate.cabinType.toLowerCase() === option.cabinType.toLowerCase()),
          cabinRate => cabinRate.priceDetails.price,
          'asc',
        )[0]

        if (!cabinRate) {
          return
        }

        return {
          occupancy,
          sessionId: rateList.sessionId,
          bookingId: rateList.bookingId,
          cabinRate,
        }
      }))

      const validRateSelections = rateSelections.filter(Boolean) as Array<WeddingGiftRegistryCruiseCabinRateSelection>

      if (validRateSelections.length === 0) {
        return
      }

      resolve({
        ...option,
        rateSelections: validRateSelections,
      })
    }
    finally {
      setLoadingCabinDetails(false)
    }
  }, [bookingRequestParams, departureId, occupancies, offer, resolve])

  const onDismiss = useCallback(() => {
    resolve(undefined)
  }, [resolve])

  return (
    <Modal
      size="L"
      title={intl.formatMessage({
        id: 'LT3HfRMS32inDKHwSsBV',
        defaultMessage: 'Choose a cabin type',
        description: 'Modal title for choosing a cruise cabin type before adding a cruise to a wedding gift registry cart',
      })}
      onClose={onDismiss}
    >
      <ModalContent>
        {loadingCabinDetails && <LoadingOverlay>
          <Group direction="vertical" gap={12} horizontalAlign="center">
            <LoadingAnimation size={10} />
            <BodyText variant="medium" weight="bold" align="center">
              {intl.formatMessage({
                id: 'NZKzcqpWLFugJlvSg_rR',
                defaultMessage: 'Retrieving cabin details',
                description: 'Loading step message when retrieving cruise cabin details',
              })}
            </BodyText>
          </Group>
        </LoadingOverlay>}

        <CruiseCabinTypeGrid>
          {options.map(option => (
            <CruiseCabinTypeTileWrapper
              key={option.cabinType}
              direction="vertical"
              gap={16}
            >
              <Group gap={16} direction="horizontal" fullHeight>
                {option.imageId && (
                  <Image
                    id={option.imageId}
                    aspectRatio="3:2"
                    width={100}
                    height={80}
                  />
                )}

                <VerticalSpacer gap={8}>
                  <Heading variant="heading4">
                    {cabinTypeLabels[option.cabinType] ?? option.cabinType}
                  </Heading>
                  <BodyText variant="small" colour="light-foreground-alt">
                    {cabinDescriptions[option.cabinType]}
                  </BodyText>
                </VerticalSpacer>
              </Group>

              <Group direction="horizontal" verticalAlign="end" horizontalAlign="space-between" gap={16}>
                <Group direction="vertical" gap={2}>
                  <PriceRowPriceCaption>
                    {intl.formatMessage({
                      id: 'ztx7w5COc_lTfnGFTwWn',
                      defaultMessage: 'Total for {travellerCount} travellers',
                      description: 'Price caption for total cruise cabin type price in wedding gift registry selection modal',
                    }, { travellerCount })}
                  </PriceRowPriceCaption>
                  <BodyText variant="large" weight="bold">
                    <FormatCurrency value={option.price.total * travellerCount} />
                  </BodyText>
                </Group>

                <TextButton
                  kind="primary"
                  disabled={loadingCabinDetails}
                  onClick={() => onSelect(option)}
                >
                  {intl.formatMessage({
                    id: 'zhpmiq0QG4j1S_VamdzF',
                    defaultMessage: 'Select',
                    description: 'Select button text for cabin type in cruise checkout',
                  })}
                </TextButton>
              </Group>
            </CruiseCabinTypeTileWrapper>
          ))}
        </CruiseCabinTypeGrid>
      </ModalContent>
    </Modal>
  )
}

export default WeddingGiftRegistryCruiseCabinTypeModal
